// An article's outline: the sections a long review or feature is made of, so the reader can show
// where you are and jump. Found in the page as it opens (after enhanceArticle), on the web and in
// the phone's reader page alike, through the shared reader bundle.

/** One section: where it starts, what it is called, and whether it sits under another. */
export interface Section { el: Element; label: string; level: 1 | 2; intro?: boolean }

/** Headings that name the page around the article, not a part of it. */
const NOT_A_SECTION = /^(?:related(?: (?:stories|articles|posts|reading))?|more (?:from|on|stories|like this)|read (?:more|next)|recommended|you might also like|comments?|leave a (?:comment|reply)|share(?: this)?|newsletter|sign up.*|subscribe.*|advertisement|sponsored|about the author|tags?|filed under)$/i;

/** Where a heading cannot start a section: inside a quote, a figure, a table, a box of its own. */
const NOT_IN_A_SECTION = "figure, blockquote, table, li, aside, nav, .box, .pc, .verdict, .post, .gallery, .credit";

const sectionText = (s: string) => s.replace(/\s+/g, " ").trim().replace(/:$/, "");
const shortLabel = (s: string) => (s.length > 120 ? s.slice(0, s.lastIndexOf(" ", 117) > 60 ? s.lastIndexOf(" ", 117) : 117) + "…" : s);
const headingRank = (el: Element) => Number(el.tagName.slice(1));

/**
 * The sections of an article, in reading order, from what the page already has: its headings
 * (two levels at most), and the blocks the reader recognises (a verdict, pros and cons, a
 * specification box, a large gallery). An introduction leads when real text comes before the
 * first section. [title] is the article's own headline, which a page may repeat as a heading.
 */
export function findSections(root: Element, title = ""): Section[] {
  const own = sectionText(title).toLowerCase();
  const heads = [...root.querySelectorAll("h1, h2, h3, h4, h5")].filter((h) => {
    const t = sectionText(h.textContent ?? "");
    return t.length >= 2 && !h.closest(NOT_IN_A_SECTION) && !NOT_A_SECTION.test(t) && t.toLowerCase() !== own && !!followedByText(h, root);
  });

  // levels: the highest heading kind is the top level, the next one sits under it. One lone
  // top heading over many of the next kind is a wrapper, not a level: both count as top.
  const kinds = [...new Set(heads.map(headingRank))].sort((a, b) => a - b);
  const count = (k: number) => heads.filter((h) => headingRank(h) === k).length;
  const flat = kinds.length > 1 && count(kinds[0]!) < 2 && count(kinds[1]!) >= 3;
  const top = new Set(flat ? kinds.slice(0, 2) : kinds.slice(0, 1));
  const sections: Section[] = heads.map((h) => ({ el: h, label: shortLabel(sectionText(h.textContent ?? "")), level: top.has(headingRank(h)) ? 1 : 2 }));

  // the reader's own blocks: a verdict, pros and cons, a titled specification box, a big gallery
  const blockName = (b: Element): string | null => {
    if (b.parentElement?.closest(".verdict, .pc, .box, .post")) return null;
    if (b.matches(".verdict")) return sectionText(b.querySelector("h4")?.textContent ?? "") || "Verdict";
    if (b.matches(".pc")) return "Pros and cons";
    if (b.matches(".box.pros")) return "Pros";
    if (b.matches(".box.cons")) return "Cons";
    if (b.matches(".box")) return sectionText(b.querySelector("h4")?.textContent ?? "") || null;
    if (b.matches(".gallery") && b.querySelectorAll("img").length >= 6 && !headingJustBefore(b)) return "Gallery";
    return null;
  };
  for (const b of root.querySelectorAll(".verdict, .pc, .box, .gallery")) {
    const name = blockName(b);
    if (name && !NOT_A_SECTION.test(name)) sections.push({ el: b, label: shortLabel(name), level: 1 });
  }
  sections.sort((a, b) => (a.el.compareDocumentPosition(b.el) & 4 ? -1 : 1)); // 4: b follows a
  // a smaller heading before the first top one has nothing to sit under: it is a section of its own
  for (const s of sections) { if (s.level === 1) break; s.level = 1; }

  // an introduction, when the article says something before its first section
  const first = sections[0];
  if (first) {
    const before = [...root.querySelectorAll("p")].filter((p) => p.compareDocumentPosition(first.el) & 4 && (p.textContent ?? "").trim().length > 40);
    if (before.length >= 2) sections.unshift({ el: before[0]!, label: "Introduction", level: 1, intro: true });
  }
  return sections;
}

/** Whether a heading has text after it before the next heading (a heading left at the end is not a section). */
function followedByText(h: Element, root: Element): boolean {
  for (let n: Element | null = afterInPage(h, root); n; n = afterInPage(n, root)) {
    if (/^H[1-6]$/.test(n.tagName)) return false;
    if ((n.textContent ?? "").trim() || n.querySelector("img, figure, iframe, table")) return true;
  }
  return false;
}
/** The element after [el] in reading order: its next sibling, or its parent's, up to [root]. */
function afterInPage(el: Element, root: Element): Element | null {
  for (let n: Element | null = el; n && n !== root; n = n.parentElement) if (n.nextElementSibling) return n.nextElementSibling;
  return null;
}
function headingJustBefore(b: Element): boolean {
  const p = b.previousElementSibling;
  return !!p && (/^H[1-6]$/.test(p.tagName) || (/^H[1-6]$/.test(p.previousElementSibling?.tagName ?? "") && !(p.textContent ?? "").trim()));
}

/**
 * Whether an outline earns its place: at least three real sections (the introduction does not
 * count), in an article at least three screens long. Anything shorter reads fine without one.
 */
export const worthOutline = (sections: readonly Section[], height: number, screen: number): boolean =>
  sections.filter((s) => s.level === 1 && !s.intro).length >= 3 && height >= 3 * screen;

/** The top of [scroller]'s visible area on screen: the window's top for the page itself. */
const viewTop = (scroller: Element) => (scroller === scroller.ownerDocument.scrollingElement ? 0 : scroller.getBoundingClientRect().top);

/**
 * The section being read: the last one whose start has passed a line a quarter of the way down
 * the screen. At the very end of the page, the last section (a short one may never reach the line).
 */
export function currentSection(sections: readonly Section[], scroller: Element): number {
  if (!sections.length) return -1;
  if (scroller.scrollTop + scroller.clientHeight >= scroller.scrollHeight - 4) return sections.length - 1;
  const line = viewTop(scroller) + scroller.clientHeight * 0.25;
  let at = 0;
  sections.forEach((s, i) => { if (s.el.getBoundingClientRect().top <= line) at = i; });
  return at;
}

/**
 * Brings a section's start to just below the top ([gap] px), or the page back to a place it was
 * ([to] as a scroll position, for "Back"). A short way scrolls there; a long way (more than a few
 * screens) goes at once with a quick fade, since a smooth scroll across a long article only
 * makes you wait. Returns where the page was.
 */
export function jumpTo(scroller: Element, to: Element | number, gap = 24): number {
  const from = scroller.scrollTop;
  const top = typeof to === "number" ? to : Math.max(0, from + to.getBoundingClientRect().top - viewTop(scroller) - gap);
  const still = matchMedia("(prefers-reduced-motion: reduce)").matches;
  if (still || Math.abs(top - from) > 2.5 * scroller.clientHeight) {
    scroller.scrollTop = top;
    const page = scroller === scroller.ownerDocument.scrollingElement ? scroller.ownerDocument.body : scroller;
    if (!still) (page.querySelector(".prose") ?? page).animate?.([{ opacity: 0.25 }, { opacity: 1 }], { duration: 220, easing: "ease-out" });
  } else scroller.scrollTo({ top, behavior: "smooth" });
  return from;
}
